import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
import { createSceneSwitcher } from "$/game/prefabs/ui/SceneSwitcher.ts";
import { RoundedTextButton } from "$/game/prefabs/ui/RoundedTextButton.ts";

export class PetDeathScene extends BasicScene {
  constructor() {
    super("PetDeathScene");
  }

  create(): void {
    // this.soundManager.playMusic(this.keys.assets.sounds.music.home);

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const centerX = width / 2;
    const centerY = height / 2;

    this.add.text(
      centerX,
      150,
      this.keys.strings.yourPetDead,
      {
        fontSize: "43px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    ).setOrigin(0.5).setAlign("center");

    this.add.text(
      centerX,
      250,
      this.keys.strings.savePet,
      {
        fontSize: "48px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.danger.toString(16),
      },
    ).setOrigin(0.5).setAlign("center");

    this.add.image(
      centerX,
      500,
      this.atlas,
      this.keys.assets.sprites
        .pets[
          this.storage.pet().type as keyof typeof this.keys.assets.sprites.pets
        ].dead,
    ).setOrigin(0.5, 0.5);

    this.add.text(
      centerX,
      700,
      this.keys.strings.saveProgress,
      {
        fontSize: "24px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    ).setOrigin(0.5).setAlign("center");

    // Ad-based resurrection button (left)
    const resurrectByAdButton = new RoundedTextButton(
      this,
      centerX - 150, // Moved to the left
      767,
      this.keys.strings.resurrectButton,
      250,
      96,
      25,
      this.global.TUNER_TWA_CONFIG.palette.warn,
      this.global.TUNER_TWA_CONFIG.palette.warn + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    )
      .on("pointerdown", () => {
        this.soundManager.playSound(this.keys.assets.sounds.penClick, {
          volume: 0.25,
        });
        this.handleResurrectByAd();
      });

    // Star-based resurrection button (right)
    const resurrectButton = new RoundedTextButton(
      this,
      centerX + 150, // Moved to the right
      767,
      this.keys.strings.resurrectButton,
      250,
      96,
      25,
      this.global.TUNER_TWA_CONFIG.palette.success,
      this.global.TUNER_TWA_CONFIG.palette.success + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    )
      .on("pointerdown", () => {
        this.soundManager.playSound(this.keys.assets.sounds.penClick, {
          volume: 0.25,
        });
        this.handleResurrect();
      });
    // Text indicating that an ad will be shown for ad-based resurrection
    this.add.text(
      centerX - 150, // Position below the ad resurrection button
      830,
      this.keys.strings.statRestoreOverlay.adWarningText || "ad will be shown",
      {
        fontSize: "20px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    ).setOrigin(0.5).setAlign("center");

    this.add.text(
      centerX,
      870,
      this.keys.strings.or,
      {
        fontSize: "24px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    ).setOrigin(0.5).setAlign("center");

    const createButton = new RoundedTextButton(
      this,
      centerX,
      950,
      this.keys.strings.createNewButton,
      353,
      72,
      25,
      this.global.TUNER_TWA_CONFIG.palette.danger,
      this.global.TUNER_TWA_CONFIG.palette.danger + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    ).on("pointerdown", () => {
      this.soundManager.playSound(this.keys.assets.sounds.penClick, {
        volume: 0.25,
      });
      this.transition.toScene(
        "PetCreateScene",
        this.keys.strings.sceneTransitions.choosePet,
      );
    });

    this.add.text(
      centerX,
      1005,
      this.keys.strings.loseProgress,
      {
        fontSize: "24px",
        fontFamily: "Phantom-Muff",
        color: "#" + this.global.TUNER_TWA_CONFIG.palette.text.toString(16),
      },
    ).setOrigin(0.5).setAlign("center");

    createSceneSwitcher(this);
  }

  private async handleResurrectByAd(): Promise<void> {
    try {
      // Show the ad first
      const adResult = await this.telegram.showAd("revive");

      if (adResult) {
        // Show loading overlay
        this.overlay.show("LoadingOverlay", false);

        // Call the API to revive the pet via ad
        const response = await this.api.pet.reviveByAd();

        if (response.data && response.data.id) {
          // Store the pet in registry and transition to PetHomeScene
          this.registry.set("pet", response.data);
          this.overlay.close("LoadingOverlay");

          this.transition.toScene(
            "PetHomeScene",
            this.keys.strings.sceneTransitions.petIsBack,
          );
        } else {
          this.overlay.close("LoadingOverlay");
          console.error("Failed to revive pet by ad");
        }
      } else {
        console.log("Ad was not watched to completion");
      }
    } catch (error) {
      console.error("Error reviving pet by ad:", error);
    }
  }

  private async handleResurrect(): Promise<void> {
    try {
      // Create the invoice using the API module
      this.overlay.show("LoadingOverlay", false);
      const response = await this.api.payment.createPetReviveInvoice();
      this.overlay.close("LoadingOverlay");
      const data = response.data;

      if (data.invoiceLink) {
        // Open the invoice
        if (this.global.TWA) {
          this.global.TWA.openInvoice(
            data.invoiceLink,
            async (status: string) => {
              console.log("Invoice status:", status);
              if (status === "paid") {
                console.log("Payment successful!");
                // Fetch the pet and transition to home screen
                this.overlay.show("LoadingOverlay", false);
                await this.fetchAndLoadPet();
                this.overlay.close("LoadingOverlay");
              } else if (status === "failed") {
                console.log("Payment failed!");
              } else if (status === "pending") {
                console.log("Payment pending...");
              }
            },
          );
        }
      } else {
        console.error("No invoice link received");
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
    }
  }

  private async fetchAndLoadPet(): Promise<void> {
    try {
      const response = await this.api.pet.get();
      const pet = response.data;

      if (pet && pet.id) {
        // Store the pet in registry and transition to PetHomeScene
        this.registry.set("pet", pet);
        this.transition.toScene(
          "PetHomeScene",
          this.keys.strings.sceneTransitions.petIsBack,
        );
      } else {
        console.error("Failed to fetch pet");
      }
    } catch (error) {
      console.error("Error fetching pet:", error);
    }
  }
}
